import { type PasswordConfig } from '../../../shared/types';
import { type ValidationResults } from '../types';

type ValidatorMap = {
  [K in keyof Required<PasswordConfig>]: (
    password: string,
    configValue: number | string
  ) => boolean;
};

/**
 * Validates a password using the provided configuration
 * @param password - The password to validate
 * @param config - The configuration to use for validation
 * @returns An object containing the validation results
 */
export function validatePassword(
  password: string,
  config: PasswordConfig
): ValidationResults {
  const results = {
    pass: [],
    fail: [],
    skip: [],
  } as ValidationResults;

  (Object.keys(config) as (keyof PasswordConfig)[]).forEach((key) => {
    const value = config[key];

    if (value === undefined || value === null) {
      // The key should be skipped if it has no config
      results.skip.push(key);

      return;
    }

    const validator = VALIDATORS[key];

    if (validator(password, value)) {
      results.pass.push(key);
    } else {
      results.fail.push(key);
    }
  });

  return results;
}

const CHARSETS = {
  upperCaseSet: 'A-Z', // All UpperCase (Acii/Unicode)
  lowerCaseSet: 'a-z', // All LowerCase (Acii/Unicode)
  numberSet: '0-9', // All numbers (Acii/Unicode)
  specialSet: '\\x20-\\x2F\\x3A-\\x40\\x5B-\\x60\\x7B-\\x7E\\x80-\\xFF', // All Other printable Ascii
} as const;

// Exported only for testing purposes. Should not be exported from utils
export const VALIDATORS = {
  minLength: (password: string, configValue: number) =>
    password.length >= configValue,
  maxLength: (password: string, configValue: number) =>
    password.length <= configValue,
  minUpperCase: (password: string, configValue: number) =>
    (password.match(new RegExp(`[${CHARSETS.upperCaseSet}]`, 'g')) ?? [])
      .length >= configValue,
  minLowerCase: (password: string, configValue: number) =>
    (password.match(new RegExp(`[${CHARSETS.lowerCaseSet}]`, 'g')) ?? [])
      .length >= configValue,
  minAlpha: (password: string, configValue: number) =>
    (
      password.match(
        new RegExp(`[${CHARSETS.lowerCaseSet}${CHARSETS.upperCaseSet}]`, 'g')
      ) ?? []
    ).length >= configValue,
  minNumeric: (password: string, configValue: number) =>
    (password.match(new RegExp(`[${CHARSETS.numberSet}]`, 'g')) ?? []).length >=
    configValue,
  minAlphaneumeric: (password: string, configValue: number) =>
    (
      password.match(
        new RegExp(
          `[${CHARSETS.lowerCaseSet}${CHARSETS.upperCaseSet}${CHARSETS.numberSet}]`,
          'g'
        )
      ) ?? []
    ).length >= configValue,
  minSpecial: (password: string, configValue: number) =>
    (password.match(new RegExp(`[${CHARSETS.specialSet}]`, 'g')) ?? [])
      .length >= configValue,
  minUnique: (password: string, configValue: number) => {
    const uniqueChars = new Set(password.split('')).size;

    return uniqueChars >= configValue;
  },
  maxRepeats: (password: string, configValue: number) => {
    const matches = password.match(/(.)\1+/g) ?? [];

    return matches.every((match) => match.length <= configValue);
  },
  regex: (password: string, configValue: string) => {
    const regex = new RegExp(configValue);

    return regex.test(password);
  },
  regexDescription: () => true,
} as ValidatorMap;
